import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert, Box, Button, Chip, Divider, FormControl, InputLabel, LinearProgress,
  MenuItem, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import { BookmarkBtn, DifficultyChip, ExecutionResultPanel, PALETTE, SectionPaper } from "../components/shared";
import { DEFAULT_JUDGE0_LANGUAGE, getBoilerplateKey } from "../utils/judge0Languages";
import { LANGUAGES } from "../hooks/useCodingPlatform";

type Props = {
  problem: any;
  language: typeof LANGUAGES[0];
  code: string;
  customInput: string;
  execution: any;
  running: boolean;
  onLanguageChange: (lang: typeof LANGUAGES[0]) => void;
  onCodeChange: (v: string) => void;
  onCustomInputChange: (v: string) => void;
  onRun: () => void;
  onSubmit: () => void;
  onToggleBookmark: (id: string) => void;
  onOpenIde: (problem: any) => void;
  onBack?: () => void;
};

export default function ProblemWorkspaceView({
  problem, language, code, customInput, execution, running,
  onLanguageChange, onCodeChange, onCustomInputChange,
  onRun, onSubmit, onToggleBookmark, onOpenIde, onBack,
}: Props) {
  if (!problem) return (
    <Paper sx={{ p: 4, textAlign: "center", borderRadius: "12px", border: `1px solid ${PALETTE.border}` }}>
      <Typography color="text.secondary" sx={{ fontSize: "14px" }}>Select a problem from the explorer to start coding.</Typography>
    </Paper>
  );

  const id = problem.id ?? problem._id;

  // ── Full-screen split layout (when opened as overlay) ──────────────────────
  if (onBack) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)" }}>
        {/* Top bar */}
        <Paper sx={{ px: 2, py: 1, mb: 1, borderRadius: "12px", border: `1px solid ${PALETTE.border}`, display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", fontSize: "12px", color: PALETTE.textSecondary }}>
            Back to Problems
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography sx={{ fontWeight: 700, fontSize: "14px", flex: 1 }} noWrap>{problem.title}</Typography>
          <DifficultyChip difficulty={problem.difficulty} />
          <BookmarkBtn bookmarked={!!problem.bookmarked} onClick={() => onToggleBookmark(id)} />
          {/* <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => onOpenIde(problem)}
            sx={{ textTransform: "none", fontSize: "12px" }}>Open IDE</Button> */}
        </Paper>

        {/* Split body */}
        <Box sx={{ display: "flex", flex: 1, gap: 1.5, minHeight: 0 }}>
          {/* ── Left: Problem statement ── */}
          <Paper sx={{ flex: "0 0 45%", borderRadius: "12px", border: `1px solid ${PALETTE.border}`, overflow: "auto", p: 2.5 }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 900, color: "primary.main", mb: 1 }}>{problem.title}</Typography>
            <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: "wrap" }}>
              <DifficultyChip difficulty={problem.difficulty} />
              <Chip size="small" label={`${problem.acceptanceRate ?? 0}% acceptance`} sx={{ fontSize: "11px", height: "22px" }} />
              {(problem.tags ?? []).slice(0, 3).map((t: string) => (
                <Chip size="small" key={t} label={t} sx={{ fontSize: "11px", height: "22px" }} />
              ))}
            </Stack>

            <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "13px", color: "#374151", lineHeight: 1.7 }}>
              {problem.description}
            </Typography>

            {problem.constraints && (
              <Alert severity="info" sx={{ mt: 2, "& .MuiAlert-message": { fontSize: "12px" } }}>
                <strong>Constraints:</strong> {problem.constraints}
              </Alert>
            )}

            {!!(problem.publicTestCases?.length) && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "13px", mb: 1 }}>Examples</Typography>
                {problem.publicTestCases.map((tc: any, i: number) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: "8px", bgcolor: PALETTE.bgLight }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }}>{tc.name ?? `Example ${i + 1}`}</Typography>
                    <Box component="pre" sx={{ m: 0, fontSize: "12px", fontFamily: "monospace", whiteSpace: "pre-wrap", color: "#1e293b" }}>
                      {`Input: ${tc.input}\nExpected: ${tc.expected}`}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            {!!(problem.hints?.length) && (
              <Alert severity="warning" sx={{ mt: 2, "& .MuiAlert-message": { fontSize: "12px" } }}>
                💡 {problem.hints[0]}
              </Alert>
            )}
          </Paper>

          {/* ── Right: Code editor + results ── */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, overflow: "auto", minHeight: 0 }}>
            <Paper sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}`, p: 1.5, flexShrink: 0 }}>
              {/* Editor toolbar */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel sx={{ fontSize: "12px" }}>Language</InputLabel>
                  <Select label="Language" value={language.value} sx={{ fontSize: "12px" }}
                    onChange={(e) => {
                      const next = LANGUAGES.find((l) => l.value === e.target.value) ?? DEFAULT_JUDGE0_LANGUAGE;
                      onLanguageChange(next);
                      onCodeChange(problem.boilerplateCode?.[getBoilerplateKey(next)] ?? next.boilerplate);
                    }} MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}>
                    {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value} sx={{ fontSize: "12px" }}>{l.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} disabled={running} onClick={onRun}
                    sx={{ textTransform: "none", fontSize: "12px" }}>Run</Button>
                  <Button size="small" variant="contained" startIcon={<SendIcon />} disabled={running} onClick={onSubmit}
                    sx={{ textTransform: "none", fontSize: "12px" }}>Submit</Button>
                </Stack>
              </Stack>

              {/* Code textarea */}
              <TextField multiline minRows={18} fullWidth value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                sx={{ "& textarea": { fontFamily: "Consolas, Monaco, monospace", fontSize: "13px", lineHeight: 1.5 },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />

              {running && <LinearProgress sx={{ mt: 0.75, borderRadius: 1 }} />}
            </Paper>

            {/* Custom input */}
            <Paper sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}`, p: 1.5, flexShrink: 0 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: PALETTE.textMuted, mb: 0.75 }}>Custom Input (for Run mode)</Typography>
              <TextField multiline minRows={2} fullWidth placeholder="Enter custom input..."
                value={customInput} onChange={(e) => onCustomInputChange(e.target.value)}
                sx={{ "& textarea": { fontFamily: "monospace", fontSize: "12px" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />
            </Paper>

            {/* Execution results */}
            {execution && (
              <Paper sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}`, p: 1.5, flexShrink: 0 }}>
                <ExecutionResultPanel execution={execution} />
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Fallback: stacked layout (legacy, kept for any other usage) ────────────
  return (
    <Stack spacing={1.5}>
      <SectionPaper>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, mr: 1 }}>
              <Typography sx={{ fontSize: "18px", fontWeight: 900, color: "primary.main" }}>{problem.title}</Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap" }}>
                <DifficultyChip difficulty={problem.difficulty} />
                <Chip size="small" label={`${problem.acceptanceRate ?? 0}% acceptance`} sx={{ fontSize: "11px", height: "22px" }} />
                {(problem.companies ?? []).slice(0, 3).map((c: string) => (
                  <Chip size="small" key={c} label={c} sx={{ fontSize: "11px", height: "22px" }} />
                ))}
              </Stack>
            </Box>
            <BookmarkBtn bookmarked={!!problem.bookmarked} onClick={() => onToggleBookmark(id)} />
          </Stack>
          <Typography sx={{ mt: 2, whiteSpace: "pre-wrap", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
            {problem.description}
          </Typography>
          {problem.constraints && (
            <Alert severity="info" sx={{ mt: 1.5, "& .MuiAlert-message": { fontSize: "12px" } }}>{problem.constraints}</Alert>
          )}
        </Box>
      </SectionPaper>

      <SectionPaper>
        <Box sx={{ p: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ width: { xs: "100%", sm: 160 } }}>
              <InputLabel sx={{ fontSize: "12px" }}>Language</InputLabel>
              <Select label="Language" value={language.value} sx={{ fontSize: "12px" }}
                onChange={(e) => {
                  const next = LANGUAGES.find((l) => l.value === e.target.value) ?? DEFAULT_JUDGE0_LANGUAGE;
                  onLanguageChange(next);
                  onCodeChange(problem.boilerplateCode?.[getBoilerplateKey(next)] ?? next.boilerplate);
                }} MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}>
                {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value} sx={{ fontSize: "12px" }}>{l.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => onOpenIde(problem)}
                sx={{ textTransform: "none", fontSize: "12px" }}>Open IDE</Button>
              <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} disabled={running} onClick={onRun}
                sx={{ textTransform: "none", fontSize: "12px" }}>Run</Button>
              <Button size="small" variant="contained" startIcon={<SendIcon />} disabled={running} onClick={onSubmit}
                sx={{ textTransform: "none", fontSize: "12px" }}>Submit</Button>
            </Stack>
          </Stack>
          <TextField multiline minRows={14} fullWidth value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            sx={{ "& textarea": { fontFamily: "Consolas, Monaco, monospace", fontSize: "13px", lineHeight: 1.5 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />
          <TextField multiline minRows={2} fullWidth placeholder="Custom input (for Run mode)"
            value={customInput} onChange={(e) => onCustomInputChange(e.target.value)}
            sx={{ mt: 1, "& textarea": { fontFamily: "monospace", fontSize: "12px" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }} />
          {running && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
        </Box>
      </SectionPaper>

      {execution && <ExecutionResultPanel execution={execution} />}
    </Stack>
  );
}
